import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import useUser from '../../hooks/useUser';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import { pushEvent, trackGoogleAdsLeadConversion } from '../../utils/analytics';
import Reveal from '../../components/course/Reveal';
import PromoCountdown from '../../components/course/PromoCountdown';
import ScrollProgress from '../../components/course/ScrollProgress';
import CourseLegalDisclaimer from '../../components/course/CourseLegalDisclaimer';
import {
  SmsConsentCheckbox,
  buildSmsConsentPayload,
  shouldShowSmsConsent,
} from '../../components/SmsConsent';
import HowFoundUsDropdown from '../../components/CheckoutFormShared/HowFoundUsDropdown';
import { sanitizeInternalDestination } from '../../utils/postSignupJourney';
import { getUtmParamsForApi } from '../../utils/utmTracker';
import {
  buildChallengeCalendarLinks,
  AI_TRADING_CHALLENGE_NAME,
  canonicalChallengeRunName,
  canonicalChallengeSessionTitle,
  challengeSlugMatches,
  formatChallengeDate,
  formatChallengeDateTime,
  pickKickoffAndDemo,
  isChallengeReplayAvailable,
  splitDisplayName,
} from './challengePageUtils';
import '../courses/CoursePage.css';
import './ChallengePage.css';

const LIVE_COURSE_PATH = '/courses/ai-trading-bootcamp';
// Promise framing (2026-08-07 research, round 2): lead with Claude by name and
// play to greed. Keyword Planner (US, Jun 2026): "claude code" gets 550,000
// searches/mo (peaked 1M in March) vs 40/mo for "ai stock research". The
// highest-engagement X formula (262k views) is "Claude runs stock research
// like a top consulting firm, for free. Prompts that replace $100K/year
// analysts." Seven-deadly-sins doctrine (Reid Hoffman via Mark Pincus):
// BotSpot's sin is greed, so the hook is money: $100K of analyst labor on
// the reader's own portfolio. Legal line: capability and labor-cost
// comparisons only, never promised returns and never "we make you money".
// Dropped "unpaid": Claude itself is paid. The point is analyst labor cost,
// not pretending Claude is free labor.
// Closing live session label. The clinic promises help and project reviews
// without requiring anyone to present a finished project.
const WRAP_UP_LABEL = 'Live Wrap-Up';

const youtubeLiveUrl = (session) => session?.materials?.find(
  (material) => material?.type === 'youtube_live' && /^https:\/\//i.test(material?.url || ''),
)?.url || null;

// Registration-page length policy (2026-08-07): our own PostHog test
// SALES_PAGE_DESIGN ended with the minimal hero+form control at a 26.59% Lead
// rate vs 21.50% for the decorated animated/FAQ variant and 9.68% for the
// video variant. Free lead capture converts best short: everything a visitor
// needs to say yes stays above or one scroll from the form.
const BOARD_STEPS = [
  {
    label: 'Night 1',
    title: 'Live kickoff',
    body: 'We set up your AI trading workflow together on camera. You leave with one clear project for the week.',
  },
  {
    label: 'During the week',
    title: 'One project, with tips',
    body: 'Keep improving the same workflow. Short emails and optional texts add tips and encouragement, not seven homework assignments.',
  },
  {
    label: WRAP_UP_LABEL,
    title: 'Finish live with Rob',
    body: 'Come back live to review the workflow, get unstuck, compare trades, and see the competition path. Finished is optional.',
  },
];

const INCLUDES = [
  { name: '2 live sessions with Rob', detail: `Kickoff plus ${WRAP_UP_LABEL.toLowerCase()} on Zoom` },
  { name: 'One AI trading workflow', detail: 'Research, define risk, and prepare one reviewed trade' },
  { name: 'Challenge hub', detail: 'Live links and calendar links for registrants' },
  { name: 'Tips by email', detail: 'Encouragement and next-step tips during the week' },
  { name: 'Optional text reminders', detail: 'Only if you agree to marketing texts' },
];

// Five objection-killers only. Time commitment lives in the board copy, so it
// is not repeated here.
const FAQS = [
  { q: 'Is this really free?', a: 'Yes. Both live sessions and the week of tip emails are free. No credit card.' },
  { q: 'Do I need to know how to code?', a: 'No. You can use Claude, Codex, or BotSpot in plain English.' },
  { q: 'Do I need a broker?', a: 'No. You can prepare a paper trade. If you already use a broker, you can work with your own account under your control.' },
  { q: 'What if I miss the kickoff?', a: 'Join the Live Wrap-Up and use the same hub. If an approved recording is published, it will appear there.' },
  { q: 'Will you pitch something?', a: `You will work on something real in both sessions. At the end of ${WRAP_UP_LABEL.toLowerCase()}, Rob invites you to the paid live October course. That is it.` },
];

const RegistrationForm = ({
  form,
  setForm,
  onSubmit,
  submitting,
  error,
  disabled,
  disabledReason,
  ctaLabel,
  testId,
  requireAttribution,
  postPurchase = false,
}) => (
  <form
    className={`cp-form chp-form-card${postPurchase ? ' chp-form-card--post-purchase' : ''}`}
    onSubmit={onSubmit}
    data-testid={testId}
  >
    <h3>{postPurchase ? 'Join the free challenge' : 'Save your free seat'}</h3>
    <label>
      Your name
      <input
        type="text"
        required
        autoComplete="name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        disabled={disabled}
      />
    </label>
    {requireAttribution ? (
      <HowFoundUsDropdown
        id={`${testId}-how-found-us`}
        value={form.howFoundUs}
        otherText={form.howFoundUsDetails}
        onChange={(howFoundUs) => setForm({
          ...form,
          howFoundUs,
          howFoundUsDetails: howFoundUs === 'other' ? form.howFoundUsDetails : '',
        })}
        onOtherTextChange={(howFoundUsDetails) => setForm({ ...form, howFoundUsDetails })}
        disabled={disabled || submitting}
      />
    ) : null}
    <label>
      Email
      <input
        type="email"
        required
        autoComplete="email"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        disabled={disabled}
      />
    </label>
    {/* Phone and SMS consent are optional. Name + email are enough to save a
        seat. If they add a phone, show full marketing SMS consent and persist
        accepted OR declined. Never force a number or a text opt-in. */}
    <label>
      Mobile number (optional)
      <input
        type="tel"
        autoComplete="tel"
        value={form.phone}
        onChange={(e) => {
          const phone = e.target.value;
          setForm({
            ...form,
            phone,
            // Clearing the phone clears the consent checkbox so a stale
            // checked state cannot ride along with an empty number.
            marketingSms: shouldShowSmsConsent(phone) ? form.marketingSms : false,
          });
        }}
        disabled={disabled}
        placeholder="+1 555 555 5555"
      />
    </label>
    <SmsConsentCheckbox
      id={`${testId}-marketing-sms`}
      checked={form.marketingSms}
      onChange={(checked) => setForm({ ...form, marketingSms: checked })}
      disabled={disabled || !form.phone.trim()}
      tone="dark"
      compact
    />
    {postPurchase ? null : (
      <label>
        Market or trade you want to work on live (optional)
        <input
          type="text"
          value={form.question}
          onChange={(e) => setForm({ ...form, question: e.target.value })}
          disabled={disabled}
          placeholder="A stock, ETF, option, crypto, or trade idea"
        />
      </label>
    )}
    {disabled && disabledReason ? (
      <p className="chp-unavailable" role="status">{disabledReason}</p>
    ) : null}
    <button
      type="submit"
      className="cp-btn-primary"
      disabled={submitting || disabled}
      data-testid={`${testId}-submit`}
      title={disabled ? disabledReason : undefined}
    >
      {submitting ? 'Saving your seat...' : ctaLabel}
    </button>
    {error ? <p className="cp-note" role="alert">{error}</p> : null}
    {/* One quiet trust line instead of a pill wall; keeps the card short. */}
    <p className="chp-reassure">
      100% free · No credit card · No coding required
    </p>
  </form>
);

/**
 * Free challenge landing page and, once registered, the participant hub.
 *
 * Sessions come from the course / session-group / course-session tables.
 * If the catalog cannot load, purchase/registration is disabled loudly.
 */
const ChallengePage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const {
    user,
    isAuthenticated,
    isLoading: userLoading,
    loginWithRedirect,
    getApiToken,
  } = useUser();
  const [challenge, setChallenge] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [registered, setRegistered] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submission, setSubmission] = useState({
    description: '',
    publicLink: '',
    question: '',
    displayName: '',
    showcasePermission: false,
    screenshotFile: null,
  });
  const [submissionSaving, setSubmissionSaving] = useState(false);
  const [submissionSaved, setSubmissionSaved] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    question: '',
    phone: '',
    marketingSms: false,
    howFoundUs: '',
    howFoundUsDetails: '',
  });
  const isPostPurchase = searchParams.get('post_purchase') === 'true';
  const postPurchaseDestination = sanitizeInternalDestination(
    searchParams.get('redirectTo'),
    '/account-settings/subscriptions',
  );
  const requireAttribution = !String(user?.howFoundUs || '').trim();

  useEffect(() => {
    let mounted = true;
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';

    (async () => {
      try {
        const token = isAuthenticated
          ? await getApiToken().catch(() => null)
          : null;
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined;
        // Public filter keeps entitled cohorts from burying challenge runs.
        const listRes = await fetch(
          `${API_URL}/courses/session-groups?active=true&registrationAccess=public&limit=50`,
          { headers: authHeaders },
        );
        if (!listRes.ok) throw new Error(`session_groups_${listRes.status}`);
        const listData = await listRes.json();
        const groups = Array.isArray(listData)
          ? listData
          : (listData.sessionGroups || listData.data || []);

        const match = groups.find((group) => challengeSlugMatches(group.name, slug));
        if (!match) throw new Error('challenge_not_found');

        let detail = match;
        const detailRes = await fetch(`${API_URL}/courses/session-groups/${match.id}`, {
          headers: authHeaders,
        });
        if (detailRes.ok) {
          detail = await detailRes.json();
        }

        if (!mounted) return;
        setChallenge({ ...match, ...detail });
        setRegistered(Boolean(detail?.isRegistered || detail?.registration));
        setLoadError(null);
      } catch (err) {
        if (!mounted) return;
        setChallenge(null);
        setLoadError(
          'Challenge schedule is temporarily unavailable. Registration is disabled until dates load from the catalog.',
        );
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [slug, isAuthenticated, getApiToken]);

  useEffect(() => {
    if (user?.email) {
      setForm((prev) => {
        const profileName = [user.firstName, user.lastName].filter(Boolean).join(' ')
          || String(user.name || '').trim();
        const profilePhone = String(user.phone || '').trim();
        const shouldUseProfilePhone = !prev.phone && Boolean(profilePhone);

        return {
          ...prev,
          email: prev.email || user.email,
          name: prev.name || profileName,
          phone: prev.phone || profilePhone,
          // Consent follows the saved profile only when the saved phone is the
          // value being filled. A visitor-edited number keeps its own decision.
          marketingSms: shouldUseProfilePhone
            ? user.smsMarketingConsent === true
            : prev.marketingSms,
        };
      });
    }
  }, [user]);

  useEffect(() => {
    if (!registered || !isAuthenticated || !challenge?.id) return undefined;
    let mounted = true;
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';

    (async () => {
      try {
        const token = await getApiToken();
        if (!token) return;
        const res = await fetch(`${API_URL}/courses/session-groups/${challenge.id}/submission`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || !mounted) return;
        const payload = await res.json();
        if (!payload?.submission) return;
        setSubmission((current) => ({
          ...current,
          description: payload.submission.description || '',
          publicLink: payload.submission.publicLink || '',
          question: payload.submission.question || '',
          displayName: payload.submission.displayName || '',
          showcasePermission: payload.submission.showcasePermission === true,
        }));
        setSubmissionSaved(true);
      } catch {
        // An unavailable optional share form must not hide the challenge hub.
      }
    })();

    return () => { mounted = false; };
  }, [registered, isAuthenticated, challenge?.id, getApiToken]);

  const { kickoff, demoDay, sessions } = useMemo(
    () => pickKickoffAndDemo(challenge?.sessions || []),
    [challenge],
  );

  // List payload may only expose nextSession when detail is down.
  const kickoffAt = kickoff?.scheduledAt
    || challenge?.nextSession?.scheduledAt
    || challenge?.scheduledAt
    || null;
  const demoAt = demoDay?.scheduledAt || null;

  const kickoffLabel = formatChallengeDateTime(kickoffAt);
  const demoLabel = formatChallengeDateTime(demoAt);
  const kickoffCalendarLinks = kickoff
    ? buildChallengeCalendarLinks(
      kickoff,
      getApiBaseUrl() || 'https://api.botspot.trade',
    )
    : null;
  const registrationOpen = Boolean(challenge?.id)
    && challenge?.registrationAccess === 'public'
    && Boolean(kickoffAt);

  const details = useMemo(() => ([
    { label: 'Live kickoff', value: kickoffLabel || (isLoading ? 'Loading...' : 'Unavailable') },
    { label: 'Length', value: '7 days' },
    { label: 'Price', value: 'Free' },
    { label: WRAP_UP_LABEL, value: demoLabel || (isLoading ? 'Loading...' : 'One week later') },
  ]), [kickoffLabel, demoLabel, isLoading]);

  const register = useCallback(async (event) => {
    event.preventDefault();
    const utmData = getUtmParamsForApi() || {};
    // Clicked submit: they are a Google Ads lead. Do not wait for HTTP 201,
    // howFoundUs match, or any other server gate. Education PMax cannot learn
    // if this fire stays behind the register response.
    trackGoogleAdsLeadConversion();
    pushEvent({
      event: 'challenge_registered',
      source: window.location.pathname,
      slug,
      value: 1,
      currency: 'USD',
      utm_source: utmData.utm_source,
      utm_medium: utmData.utm_medium,
      utm_campaign: utmData.utm_campaign,
      utm_term: utmData.utm_term,
      utm_content: utmData.utm_content,
      gclid: utmData.gclid,
    });
    setSubmitting(true);
    setError(null);
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';

    try {
      if (!challenge?.id || !registrationOpen) {
        throw new Error('challenge_not_ready');
      }
      const { firstName, lastName } = splitDisplayName(form.name);
      const phone = String(form.phone || '').trim();
      const body = {
        firstName,
        lastName,
        email: form.email,
        intentQuestion: form.question || undefined,
        registrationContext: 'public_challenge',
        discoverySource: form.howFoundUs || undefined,
        discoveryDetails: form.howFoundUsDetails || undefined,
        utmSource: utmData.utm_source || undefined,
        utmMedium: utmData.utm_medium || undefined,
        utmCampaign: utmData.utm_campaign || undefined,
        utmTerm: utmData.utm_term || undefined,
        utmContent: utmData.utm_content || undefined,
        gclid: utmData.gclid || undefined,
        referrer: utmData.referrer || document.referrer || undefined,
        landingPage: utmData.landing_page || window.location.href.split('?')[0],
      };

      // Phone is optional. Only attach phone + SMS consent proof when the
      // visitor actually gave a number. Unchecked still means an explicit
      // marketing decline for that number. No phone => no SMS consent row.
      if (phone) {
        body.phone = phone;
        // ABSOLUTE: customer SMS opt-in is ONLY full marketing consent.
        // Never send a separate event-only / "this challenge only" consent
        // payload. Node persists user.smsMarketingConsent* +
        // sms_marketing_consent_event, and gates challenge reminders on that
        // same marketing decision. Lawsuit-risk ban: 2026-08-08.
        body.smsMarketingConsent = buildSmsConsentPayload({
          source: 'ai_trading_challenge_registration',
          phoneNumber: phone,
          accepted: form.marketingSms,
          pagePath: window.location.pathname,
        });
      }

      const token = isAuthenticated ? await getApiToken().catch(() => null) : null;
      const res = await fetch(`${API_URL}/courses/session-groups/${challenge.id}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.message || payload.error || 'registration_failed');
      }

      await res.json().catch(() => ({}));
      // A 201 means the seat is saved. Google Ads already fired on click.
      // Do not block the confirmation UI because Node stored a different
      // howFoundUs than the form. Guest Google traffic often lands as other.
      setRegistered(true);
      document.getElementById('challenge-top')?.scrollIntoView({ behavior: 'smooth' });
    } catch (err) {
      setError(
        err?.message && !String(err.message).includes('_')
          ? err.message
          : 'That did not go through. Please try again, or email support@botspot.trade and we will add you manually.',
      );
    } finally {
      setSubmitting(false);
    }
  }, [form, slug, challenge, registrationOpen, isAuthenticated, getApiToken]);

  const saveSubmission = useCallback(async (event) => {
    event.preventDefault();
    setSubmissionSaving(true);
    setSubmissionError(null);
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';

    try {
      const token = await getApiToken();
      if (!token) throw new Error('Sign in before sharing your project.');

      let screenshotUrl;
      if (submission.screenshotFile) {
        const uploadBody = new FormData();
        uploadBody.append('file', submission.screenshotFile);
        const uploadRes = await fetch(
          `${API_URL}/courses/session-groups/${challenge.id}/submission-image`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: uploadBody,
          },
        );
        const uploadPayload = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok) {
          throw new Error(uploadPayload.error || 'Could not upload that screenshot.');
        }
        screenshotUrl = uploadPayload.screenshotUrl;
      }

      const res = await fetch(`${API_URL}/courses/session-groups/${challenge.id}/submission`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          description: submission.description,
          publicLink: submission.publicLink || undefined,
          screenshotUrl,
          question: submission.question || undefined,
          displayName: submission.displayName || undefined,
          showcasePermission: submission.showcasePermission,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.error || 'Could not save your project.');
      setSubmissionSaved(true);
      setSubmission((current) => ({ ...current, screenshotFile: null }));
    } catch (err) {
      setSubmissionError(err?.message || 'Could not save your project.');
    } finally {
      setSubmissionSaving(false);
    }
  }, [challenge?.id, getApiToken, submission]);

  const scrollToForm = (event) => {
    event.preventDefault();
    document.getElementById('challenge-register')?.scrollIntoView({ behavior: 'smooth' });
  };

  const runTitle = canonicalChallengeRunName(challenge?.name);
  const challengeName = String(challenge?.course?.title || challenge?.name || AI_TRADING_CHALLENGE_NAME).trim();
  const pageTitle = `${challengeName} | Free Live Challenge | BotSpot`;
  const pageDescription = String(challenge?.description || '').trim()
    || 'Challenge details are currently unavailable.';
  const availableReplays = sessions.filter((session) => isChallengeReplayAvailable(session));

  return (
    <div
      className={`course-page challenge-page${isPostPurchase ? ' challenge-page--post-purchase' : ''}`}
      data-testid="challenge-page"
      id="challenge-top"
    >
      <ScrollProgress />
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>

      {isPostPurchase ? (
        <section className="chp-post-purchase" data-testid="post-purchase-challenge-context">
          <div className="chp-post-purchase__inner">
            <p className="chp-post-purchase__status">BotSpot plan active</p>
            <p className="chp-post-purchase__next">
              Join the free {challengeName}
            </p>
            <Link className="chp-post-purchase__skip" to={postPurchaseDestination}>
              Skip to BotSpot
            </Link>
          </div>
        </section>
      ) : null}

      {/* Webinar-registration layout: all the persuasion copy stacks left,
          the compact form card IS the hero visual on the right. The product
          art lives in its own full-width showcase below, where it can be big. */}
      <header className={`chp-hero-grid${isPostPurchase ? ' chp-hero-grid--post-purchase' : ''}`}>
        <div className="chp-hero-copy">
          <Reveal>
            <p className="chp-live-badge">
              <span className="chp-live-dot" aria-hidden="true" />
              Free · Seven days · Live with Rob
            </p>
          </Reveal>
          <Reveal delay={50}>
            <h1 className="chp-hero-title">{challengeName}</h1>
          </Reveal>
          <Reveal delay={90}>
            <p className="chp-hero-sub">
              {pageDescription}
            </p>
          </Reveal>
          <Reveal delay={120}>
            <p className="chp-when-line" data-testid="challenge-when">
              {kickoffLabel && demoLabel
                ? `Kickoff ${kickoffLabel} · ${WRAP_UP_LABEL} ${demoLabel}`
                : kickoffLabel
                  ? `Kickoff ${kickoffLabel}`
                  : (isLoading ? 'Loading dates from catalog...' : 'Dates unavailable from catalog')}
            </p>
          </Reveal>
          {isPostPurchase ? null : (
            <>
              <Reveal delay={140}>
                <div className="chp-host-inline">
                  <img src="/images/rob.jpg" alt="Rob Grzesik" width="52" height="52" />
                  <div>
                    <strong>Rob Grzesik</strong>
                    <span>
                      Managed $3B+ in transactions at Greystone · early Voyager Digital engineer
                      (company peaked near $1B) · 15+ years in financial technology
                    </span>
                  </div>
                </div>
              </Reveal>
              {kickoffAt ? (
                <Reveal delay={160}>
                  <PromoCountdown
                    endsAt={kickoffAt}
                    label="Kickoff starts in"
                    variant="blocks"
                  />
                </Reveal>
              ) : null}
            </>
          )}
        </div>

        <div className="chp-hero-form">
          {registered ? (
            <Reveal delay={120}>
              <div className="chp-registered" data-testid="challenge-registered">
                <h3>You are in.</h3>
                <p>
                  {kickoffLabel ? `Kickoff is ${kickoffLabel}.` : 'Your challenge seat is saved.'}
                </p>
                <div className="chp-registered-actions" aria-label="Live kickoff links">
                  {kickoff?.joinUrl ? (
                    <a
                      className="chp-live-action chp-live-action--primary"
                      href={kickoff.joinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Join live on Zoom
                    </a>
                  ) : null}
                  {youtubeLiveUrl(kickoff) ? (
                    <a
                      className="chp-live-action chp-live-action--secondary"
                      href={youtubeLiveUrl(kickoff)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Get notified on YouTube
                    </a>
                  ) : null}
                </div>
                {kickoffCalendarLinks ? (
                  <div className="chp-registered-calendar">
                    <p>Add the kickoff to your calendar</p>
                    <div className="chp-registered-calendar-actions">
                      <a
                        href={kickoffCalendarLinks.google}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="challenge-registered-google-calendar"
                      >
                        Google Calendar
                      </a>
                      <a
                        href={kickoffCalendarLinks.ics}
                        download={`${canonicalChallengeSessionTitle(kickoff.title)}.ics`}
                        data-testid="challenge-registered-ics"
                      >
                        Apple / Outlook
                      </a>
                    </div>
                  </div>
                ) : null}
                {kickoffAt ? (
                  <div className="chp-registered-countdown">
                    <PromoCountdown endsAt={kickoffAt} label="Kickoff starts in" variant="blocks" />
                  </div>
                ) : null}
              </div>
            </Reveal>
          ) : (
            <Reveal delay={120}>
              <div id="challenge-register">
                <RegistrationForm
                  form={form}
                  setForm={setForm}
                  onSubmit={register}
                  submitting={submitting}
                  error={error}
                  disabled={!registrationOpen || userLoading}
                  disabledReason={loadError || (!kickoffAt && !isLoading
                    ? 'Kickoff date is missing from the catalog, so registration is locked.'
                    : null)}
                  ctaLabel={isPostPurchase ? 'Join the free challenge' : 'Save my free seat'}
                  testId="challenge-form"
                  requireAttribution={requireAttribution && !userLoading}
                  postPurchase={isPostPurchase}
                />
              </div>
            </Reveal>
          )}
        </div>
      </header>

      <section className="chp-showcase" aria-label="What you will build">
        <Reveal>
          <img
            className="chp-showcase-art"
            src="/images/challenges/challenge-hero-dashboard.webp"
            alt="AI trading workflow with a watchlist, portfolio view, thesis, and sourced market updates"
            width="1376"
            height="768"
            loading="eager"
          />
          <p className="chp-showcase-caption">
            The project for the week: one AI trading workflow that researches the market,
            makes risk visible, and prepares a trade for your review.
          </p>
        </Reveal>
      </section>

      <section className="chp-details" aria-label="Challenge details" data-testid="challenge-details">
        <Reveal>
          <ul>
            {details.map((item) => (
              <li key={item.label}>
                <span className="chp-details-label">{item.label}</span>
                <span className="chp-details-value">{item.value}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* Single persuasion section. The outcomes / fit / preview-video / host /
          includes sections were cut per the SALES_PAGE_DESIGN result: the
          minimal control out-converted the decorated long variant for lead
          capture. What remains answers exactly one question: what happens
          during the week. */}
      <section className="cp-section" data-testid="challenge-board">
        <Reveal><h2>How the week works</h2></Reveal>
        <Reveal delay={50}>
          <p className="cp-sub">
            One project for the week: set up an AI trading workflow you can keep using.
            Live kickoff, light tips, then the live wrap-up. No seven-day homework list.
          </p>
        </Reveal>
        <ul className="chp-board-steps">
          {BOARD_STEPS.map((step, i) => (
            <Reveal as="li" key={step.title} delay={i * 70}>
              <strong>{step.label}</strong>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </Reveal>
          ))}
        </ul>
        {!registered ? (
          <div className="chp-mid-cta">
            <a href="#challenge-register" onClick={scrollToForm}>Save my free seat</a>
          </div>
        ) : null}
      </section>

      {registered ? (
        <>
          <section className="cp-section" data-testid="challenge-schedule">
            <Reveal><h2>Your live schedule</h2></Reveal>
            <ul className="chp-session-list">
              {sessions.map((session) => {
                const calendarLinks = buildChallengeCalendarLinks(
                  session,
                  getApiBaseUrl() || 'https://api.botspot.trade',
                );
                return (
                  <li key={session.id} data-testid={`challenge-session-${session.id}`}>
                    <div>
                      <strong>{canonicalChallengeSessionTitle(session.title)}</strong>
                      <span>{formatChallengeDateTime(session.scheduledAt)}</span>
                    </div>
                    <div className="chp-session-actions">
                      {session.joinUrl ? (
                        <a href={session.joinUrl} target="_blank" rel="noopener noreferrer">
                          Join on Zoom
                        </a>
                      ) : null}
                      {youtubeLiveUrl(session) ? (
                        <a
                          href={youtubeLiveUrl(session)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`challenge-youtube-live-${session.id}`}
                        >
                          Get notified on YouTube
                        </a>
                      ) : null}
                      {calendarLinks ? (
                        <>
                          <a
                            href={calendarLinks.google}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`challenge-google-calendar-${session.id}`}
                          >
                            Google Calendar
                          </a>
                          <a
                            href={calendarLinks.ics}
                            download={`${canonicalChallengeSessionTitle(session.title)}.ics`}
                            data-testid={`challenge-ics-${session.id}`}
                          >
                            Apple / Outlook
                          </a>
                        </>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="cp-section" data-testid="challenge-includes">
            <Reveal><h2>Everything you get</h2></Reveal>
            <ul className="chp-includes">
              {INCLUDES.map((item, i) => (
                <Reveal as="li" key={item.name} delay={i * 40}>
                  <strong>{item.name}</strong>
                  {' · '}
                  {item.detail}
                </Reveal>
              ))}
            </ul>
          </section>

          <section className="cp-section" data-testid="challenge-submission">
            <Reveal><h2>Share your project (optional)</h2></Reveal>
            <Reveal delay={50}>
              <p className="cp-sub">
                There is no submission requirement. Share a screenshot or public link only if
                you want feedback at the live wrap-up.
              </p>
            </Reveal>
            {isAuthenticated ? (
              <form className="chp-submission-form" onSubmit={saveSubmission}>
                <label>
                  What did you build?
                  <textarea
                    required
                    value={submission.description}
                    onChange={(event) => setSubmission({
                      ...submission,
                      description: event.target.value,
                    })}
                  />
                </label>
                <label>
                  Public link (optional if you upload a screenshot)
                  <input
                    type="url"
                    value={submission.publicLink}
                    onChange={(event) => setSubmission({
                      ...submission,
                      publicLink: event.target.value,
                    })}
                  />
                </label>
                <label>
                  Screenshot (optional if you add a public link)
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event) => setSubmission({
                      ...submission,
                      screenshotFile: event.target.files?.[0] || null,
                    })}
                  />
                </label>
                <label>
                  Question for Rob (optional)
                  <input
                    type="text"
                    value={submission.question}
                    onChange={(event) => setSubmission({
                      ...submission,
                      question: event.target.value,
                    })}
                  />
                </label>
                <label className="cp-check">
                  <input
                    type="checkbox"
                    checked={submission.showcasePermission}
                    onChange={(event) => setSubmission({
                      ...submission,
                      showcasePermission: event.target.checked,
                    })}
                  />
                  <span>BotSpot may show this project in the live wrap-up.</span>
                </label>
                <button type="submit" className="cp-btn-primary" disabled={submissionSaving}>
                  {submissionSaving ? 'Saving...' : 'Save my project'}
                </button>
                {submissionSaved ? <p role="status">Project saved.</p> : null}
                {submissionError ? <p role="alert">{submissionError}</p> : null}
              </form>
            ) : (
              <div className="chp-submission-signin">
                <p>Sign in with the same email you registered with to share your project.</p>
                <button
                  type="button"
                  className="cp-btn-primary"
                  onClick={() => loginWithRedirect({
                    appState: { returnTo: window.location.pathname },
                  })}
                >
                  Sign in to share
                </button>
              </div>
            )}
          </section>

          {availableReplays.length ? (
            <section className="cp-section">
              <Reveal><h2>Replays</h2></Reveal>
              <ul className="cp-archive">
                {availableReplays.map((s) => (
                  <Reveal as="li" key={s.id}>
                    <strong>{canonicalChallengeSessionTitle(s.title)}</strong>
                    <br />
                    <a href={s.recordingUrl} target="_blank" rel="noopener noreferrer">Watch the recording</a>
                  </Reveal>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      ) : (
        <section className="cp-section cp-faq" data-testid="challenge-faq">
          <Reveal><h2>Questions people ask</h2></Reveal>
          <dl>
            {FAQS.map((item, i) => (
              <Reveal as="div" key={item.q} delay={i * 35}>
                <dt>{item.q}</dt>
                <dd>{item.a}</dd>
              </Reveal>
            ))}
          </dl>
        </section>
      )}

      {!registered ? (
        <section className="cp-section" data-testid="challenge-final-cta">
          {/* No banner art here: the second form is the whole point of the
              section and decoration above it pushed it below the fold on
              mobile. */}
          <div className="chp-final">
            <div className="chp-final-inner">
              <Reveal>
                <h2>One week from now, your AI trading workflow is ready to use</h2>
                <p>
                  {kickoffLabel
                    ? `Kickoff ${formatChallengeDate(kickoffAt)}. Free. No credit card.`
                    : 'Join the free live challenge when dates are available.'}
                </p>
              </Reveal>
              <Reveal delay={80}>
                <RegistrationForm
                  form={form}
                  setForm={setForm}
                  onSubmit={register}
                  submitting={submitting}
                  error={error}
                  disabled={!registrationOpen || userLoading}
                  disabledReason={loadError}
                  ctaLabel="I'm in. Save my free seat"
                  testId="challenge-form-final"
                  requireAttribution={requireAttribution && !userLoading}
                />
              </Reveal>
              <p className="chp-secondary">
                Want the full five-week course after the live wrap-up?
                {' '}
                <Link to={LIVE_COURSE_PATH}>See the AI Trading Bootcamp</Link>
              </p>
            </div>
          </div>
        </section>
      ) : (
        <section className="cp-section cp-final">
          <Reveal>
            <h2>You are locked in for {runTitle}</h2>
            <p className="cp-muted">
              Watch your inbox for tips after kickoff.
              {demoLabel ? ` ${WRAP_UP_LABEL} is ${demoLabel}.` : ''}
            </p>
            {isPostPurchase ? (
              <Link className="cp-btn-primary" to={postPurchaseDestination} data-testid="post-purchase-challenge-continue">
                Continue to BotSpot
              </Link>
            ) : null}
          </Reveal>
        </section>
      )}

      <CourseLegalDisclaimer subject="This challenge" />
    </div>
  );
};

export default ChallengePage;
